import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { WeeklyOperatingHours, DayOperatingHours, createDefaultOperatingHours } from '@/components/common/WeeklyOperatingHours';
import { sellerService } from '@/services/seller';
import { getRestaurantStatus, toggleRestaurantStatus } from '@/services/seller/seller-store';
import { useToast } from '@/components/common/Toast';
import { useSellerStore } from '@/store/seller';
import { BusinessStatusModal } from '@/components/seller/BusinessStatusModal';

export function DesktopOperatingHoursPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { setLoading, setError } = useSellerStore();

  const [restaurantId, setRestaurantId] = useState<string>('');
  const [operatingHours, setOperatingHours] = useState<DayOperatingHours[]>(createDefaultOperatingHours());
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  useEffect(() => {
    loadOperatingHours();
  }, []);

  const loadOperatingHours = async () => {
    try {
      setLoading(true);
      
      // 1. 레스토랑 ID 가져오기
      const restaurantResult = await sellerService.getSellerRestaurantId();
      if (!restaurantResult.success || !restaurantResult.restaurantId) {
        setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
        return;
      }

      setRestaurantId(restaurantResult.restaurantId);

      // 2. 영업 상태 가져오기
      const statusResult = await getRestaurantStatus(restaurantResult.restaurantId);
      if (statusResult.success && statusResult.data) {
        setIsOpen(statusResult.data.is_open);
      }

      // 3. 영업시간 정보 가져오기
      const hoursResult = await sellerService.getOperatingHours(restaurantResult.restaurantId);
      if (hoursResult.success && hoursResult.data && Array.isArray(hoursResult.data)) {
        setOperatingHours(hoursResult.data);
      } else {
        // 데이터가 없거나 올바르지 않은 경우 기본값 유지
        console.log('No operating hours data found, using default values');
        setOperatingHours(createDefaultOperatingHours());
      }
      
    } catch (error) {
      console.error('Operating hours load error:', error);
      setError('영업시간 정보를 불러오는 중 오류가 발생했습니다');
      // 에러 발생 시에도 기본값 사용
      setOperatingHours(createDefaultOperatingHours());
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) {
      showToast('레스토랑 정보를 찾을 수 없습니다');
      return;
    }

    try {
      setIsSaving(true);

      const result = await sellerService.updateOperatingHours(restaurantId, operatingHours);
      
      if (result.success) {
        showToast('영업시간이 성공적으로 저장되었습니다');
      } else {
        showToast(result.error || '영업시간 저장에 실패했습니다');
      }
    } catch (error) {
      console.error('Operating hours save error:', error);
      showToast('영업시간 저장 중 오류가 발생했습니다');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusToggle = () => {
    setShowStatusModal(true);
  };

  const handleConfirmStatusToggle = async () => {
    if (!restaurantId) return;

    try {
      setIsTogglingStatus(true);
      const newStatus = !isOpen;
      
      const result = await toggleRestaurantStatus(restaurantId, newStatus);
      
      if (result.success) {
        setIsOpen(newStatus);
        setShowStatusModal(false);
        showToast(newStatus ? '영업을 시작했습니다' : '영업을 중단했습니다');
      } else {
        showToast(result.error || '영업 상태 변경에 실패했습니다');
      }
    } catch (error) {
      console.error('Status toggle error:', error);
      showToast('영업 상태 변경 중 오류가 발생했습니다');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-800 mr-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">영업시간 설정</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleStatusToggle}
              disabled={!restaurantId}
              className={`px-6 py-3 text-[18px] font-semibold rounded-xl transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${
                isOpen 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {isOpen ? '영업 중단하기' : '영업 시작하기'}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !restaurantId}
              className="px-8 py-3 bg-blue-500 text-white text-[18px] font-semibold rounded-xl hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isSaving ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </div>

        {/* Operating Hours Settings */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <div className="mb-6">
            <h2 className="text-[24px] font-semibold text-gray-800 mb-2">운영시간 설정</h2>
            <p className="text-[16px] text-gray-600">
              각 요일별로 운영시간과 브레이크타임을 설정할 수 있습니다. 
              '모두 적용' 버튼을 사용하여 다른 요일에 동일한 설정을 적용할 수 있습니다.
            </p>
          </div>

          {/* 주요 안내사항 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="text-[18px] font-semibold text-blue-800 mb-2">운영시간 설정 안내</h3>
            <ul className="space-y-1 text-[16px] text-blue-700">
              <li>• 마감할인: 마감할인 수령시작시간 30분 전까지 주문 가능</li>
              <li>• 예약할인: 전날 영업종료 1시간 전까지 주문 접수</li>
              <li>• 일반주문: 영업시간 내 언제든 주문 가능</li>
              <li>• 브레이크타임 중에는 모든 주문이 일시 중단됩니다</li>
            </ul>
          </div>

          {/* Weekly Operating Hours Component */}
          <div className="max-w-3xl">
            <WeeklyOperatingHours
              operatingHours={operatingHours}
              onChange={setOperatingHours}
            />
          </div>

          {/* 추가 설정 안내 */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-[18px] font-semibold text-gray-800 mb-3">설정 완료 후</h3>
            <div className="space-y-2 text-[16px] text-gray-600">
              <p>• 영업시간 변경사항은 저장 즉시 반영됩니다</p>
              <p>• 고객에게는 다음 주문부터 새로운 영업시간이 적용됩니다</p>
              <p>• 영업시간 외 주문 접수는 자동으로 차단됩니다</p>
            </div>
          </div>
        </div>
      </div>

      <BusinessStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleConfirmStatusToggle}
        isClosing={isOpen}
        isLoading={isTogglingStatus}
      />

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}